/**
 * Icon Renderers Map - Isolated Module
 *
 * This file contains the central Map for icon renderers with NO dependencies
 * on other registry files. This prevents circular dependency issues caused by
 * ES module import hoisting.
 *
 * CRITICAL: Do NOT import from any other registry/*.ts files in this module.
 */

import type { ComponentType, ReactNode } from 'react';

import type { IconSource } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Icon Definition Interface
// ============================================================================

/**
 * Definition of an icon for use in IconPicker and AttackGraphIcon.
 */
export interface IconDefinition {
  /** Unique identifier in format 'source:name' */
  id: string;
  /** React component to render the icon */
  component: ComponentType<{ size?: number; className?: string }>;
  /** Human-readable label for UI display */
  label: string;
  /** Category for organizational grouping */
  category: string;
}

// ============================================================================
// Icon Render Props
// ============================================================================

/**
 * Props passed to icon render functions.
 */
export interface IconRenderProps {
  /** Icon size in pixels */
  size?: number;
  /** CSS class name for styling */
  className?: string;
}

// ============================================================================
// Icon Renderer Interface
// ============================================================================

/**
 * Interface for icon source renderers.
 *
 * Each icon source (aws, azure, lucide, etc.) implements this interface
 * to provide rendering, validation, and enumeration capabilities.
 */
export interface IconRenderer {
  /**
   * Render an icon by name.
   *
   * @param name - Icon name within this source
   * @param props - Render props (size, className)
   * @returns React node or null if icon not found
   */
  render: (name: string, props: IconRenderProps) => ReactNode;

  /**
   * Validate that an icon name exists in this source.
   *
   * @param name - Icon name to validate
   * @returns true if icon exists, false otherwise
   */
  validate: (name: string) => boolean;

  /**
   * Get the icon component by name.
   *
   * @param name - Icon name within this source
   * @returns React component or null if not found
   */
  getIcon: (
    name: string
  ) => ComponentType<{ size?: number; className?: string }> | null;

  /**
   * Get all icons in this source for IconPicker enumeration.
   *
   * @returns Array of all icon definitions in this source
   */
  getAll: () => IconDefinition[];
}

// ============================================================================
// Registry Map - The Central Registry
// ============================================================================

/**
 * Global registry of icon renderers by source.
 *
 * Extensible - new sources can be added without modifying AttackGraphIcon.
 *
 * IMPORTANT: This Map is defined here (not in index.ts) to avoid circular
 * dependency issues. Registry files import from this module, and index.ts
 * imports registries to trigger their self-registration.
 */
export const iconRenderers = new Map<IconSource, IconRenderer>();
